// Paginated user list with search.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getUsers } from '../adminApi';
import { Loading, Err } from './Overview';

const PAGE_SIZE = 50;

export default function Users() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setData(null);
            try {
                const d = await getUsers({ q: search, page, size: PAGE_SIZE });
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load');
            }
        })();
        return () => { cancelled = true; };
    }, [search, page]);

    const onSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(q);
    };

    if (err) return <Err msg={err} />;

    return (
        <div>
            <h2 style={styles.h2}>Users</h2>
            <form onSubmit={onSearch} style={styles.searchRow}>
                <input
                    style={styles.input}
                    placeholder="Search email, name…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <button type="submit" style={styles.btn}>Search</button>
                {search ? (
                    <button
                        type="button"
                        style={styles.btnLink}
                        onClick={() => { setQ(''); setSearch(''); setPage(1); }}
                    >
                        Clear
                    </button>
                ) : null}
            </form>

            {!data ? <Loading /> : (
                <>
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <Th>Email</Th>
                                    <Th>Name</Th>
                                    <Th>Signed up</Th>
                                    <Th>Last login</Th>
                                    <Th>Subscription</Th>
                                    <Th align="right">Convos done</Th>
                                    <Th align="right">Lines</Th>
                                    <Th align="right">Avg Capeesh</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((u) => (
                                    <tr key={u.id}>
                                        <Td>
                                            <Link to={`/admin/users/${u.id}`} style={styles.link}>
                                                {u.email}
                                            </Link>
                                        </Td>
                                        <Td>{[u.fname, u.lname].filter(Boolean).join(' ') || '—'}</Td>
                                        <Td>{shortDate(u.created_at)}</Td>
                                        <Td>{shortDate(u.last_login_at)}</Td>
                                        <Td><SubBadge status={u.subscription_status} /></Td>
                                        <Td align="right">{u.n_conversations_completed}</Td>
                                        <Td align="right">{u.n_lines_spoken}</Td>
                                        <Td align="right">{u.av_capeesh_score ?? '—'}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pager
                        page={page}
                        size={PAGE_SIZE}
                        total={data.total}
                        onChange={setPage}
                    />
                </>
            )}
        </div>
    );
}

export function Pager({ page, size, total, onChange }) {
    const last = Math.max(1, Math.ceil(total / size));
    return (
        <div style={styles.pager}>
            <span style={{ color: '#888', fontSize: 13 }}>
                {Math.min((page - 1) * size + 1, total).toLocaleString()}–
                {Math.min(page * size, total).toLocaleString()} of {total.toLocaleString()}
            </span>
            <button
                style={styles.btn}
                disabled={page <= 1}
                onClick={() => onChange(page - 1)}
            >
                ← Prev
            </button>
            <span style={{ color: '#888', fontSize: 13 }}>page {page} / {last}</span>
            <button
                style={styles.btn}
                disabled={page >= last}
                onClick={() => onChange(page + 1)}
            >
                Next →
            </button>
        </div>
    );
}

function SubBadge({ status }) {
    const color = status === 'active' ? '#2e7d32' : status === 'cancelled' ? '#c0392b' : '#888';
    return (
        <span style={{ color, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {status}
        </span>
    );
}

export function shortDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function Th({ children, align }) {
    return <th style={{ ...styles.th, textAlign: align || 'left' }}>{children}</th>;
}
function Td({ children, align }) {
    return <td style={{ ...styles.td, textAlign: align || 'left' }}>{children}</td>;
}

const styles = {
    h2: { marginTop: 0, color: '#222' },
    searchRow: { display: 'flex', gap: 8, marginBottom: 12 },
    input: { flex: '0 0 280px', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc' },
    btn: { padding: '8px 14px', borderRadius: 6, border: '1px solid #d97447', background: '#fff', color: '#d97447', fontWeight: 600, cursor: 'pointer' },
    btnLink: { padding: '8px 0', border: 'none', background: 'transparent', color: '#888', cursor: 'pointer' },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 },
    td: { padding: '10px 12px', borderBottom: '1px solid #f3f3f3', color: '#222' },
    link: { color: '#d97447', textDecoration: 'none', fontWeight: 600 },
    pager: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, fontSize: 13 },
};
